import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout";
import images from "../common/images";
import Hero from "../components/Hero";
import FavouriteIcon from "../components/FavouriteIcon";
import ImageSwiper from "../components/ImagesSwiper";
import EventCard from "../components/EventCard";
import ImageCarousel from "../components/ImageCarousel";
import ClubsCarousel from "../components/ClubsCarousel";
import whatsappIcon from "../assets/img/whatsapp.png";
import telephoneIcon from "../assets/img/telephone.png";
import googleMapsIcon from "../assets/img/google-maps.png";
import BookClubForm from "../components/BookClubForm";
import ReportClubClosure from "../components/ReportClubClosure";
import { getClubPath } from "../utils/clubPath";
import { getClubsCitySlug } from "../config/seo";
import NotFound from "./notFound";
import LoadingSpinner from "../components/LoadingSpinner";
import useMobileStoreRedirect from "../hooks/useMobileStoreRedirect";

const ClubDetails = () => {
  const navigate = useNavigate();
  const [clubViewerIsOpen, setClubViewerIsOpen] = useState(false);
  const [beveragesViewerIsOpen, setBeveragesViewerIsOpen] = useState(false);
  const [foodMenuViewerIsOpen, setFoodMenuViewerIsOpen] = useState(false);
  const { city, clubSlug } = useParams();
  const clubKey = clubSlug || city;
  const { data: club, isError, isFetched, isLoading } = useQuery({
    queryKey: [`api/clubs/${clubKey}`],
    enabled: Boolean(clubKey),
    retry: false,
  });

  const clubObjectId = club?.data?._id;
  useMobileStoreRedirect("clubs", clubObjectId || clubKey);

  useEffect(() => {
    if (!club?.data) return;

    const resolvedSlug = club.data.slug || clubKey;
    const resolvedCitySlug =
      getClubsCitySlug(
        club.data.city || club.data?.address?.city || city,
      ) || "mumbai";
    const currentCitySlug = getClubsCitySlug(city) || `${city || ""}`.toLowerCase();
    const onLegacyClubsPath = window.location.pathname.startsWith("/clubs/");

    if (
      resolvedSlug &&
      (onLegacyClubsPath ||
        clubSlug !== resolvedSlug ||
        !clubSlug ||
        currentCitySlug !== resolvedCitySlug)
    ) {
      navigate(getClubPath(club.data, resolvedCitySlug), { replace: true });
    }
  }, [club?.data, clubSlug, clubKey, city, navigate]);

  const eventsQueryParams = useMemo(() => {
    if (!clubObjectId) return null;
    const queryParams = new URLSearchParams();
    queryParams.append("upcoming", 1);
    queryParams.append("page", 0);
    queryParams.append("clubId", clubObjectId);
    return queryParams.toString();
  }, [clubObjectId]);

  const { data: events } = useQuery({
    queryKey: [`api/events?${eventsQueryParams}`],
    enabled: Boolean(eventsQueryParams),
  });

  const token = localStorage.getItem("userToken");
  const isLoggedIn = JSON.parse(token);
  const [isFormOpened, setIsFormOpened] = useState(false);
  const [isClosureReportOpen, setIsClosureReportOpen] = useState(false);

  const clubCoordinates = useMemo(() => {
    const coordinates =
      club?.data?.geoJson?.coordinates ||
      club?.data?.geojson?.coordinates ||
      club?.data?.location?.coordinates ||
      club?.data?.coordinates;

    if (!Array.isArray(coordinates) || coordinates.length < 2) {
      return null;
    }

    const longitude = Number(coordinates[0]);
    const latitude = Number(coordinates[1]);

    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return null;
    }

    return `${longitude},${latitude}`;
  }, [club]);

  const nearbyClubsUrl =
    clubCoordinates && clubObjectId
      ? `api/clubs?exclude=${encodeURIComponent(
          clubObjectId,
        )}&coordinates=${clubCoordinates}`
      : null;

  const { data: clubsNearbyData, isFetching: isNearbyClubsFetching } = useQuery(
    {
      queryKey: [nearbyClubsUrl],
      enabled: Boolean(nearbyClubsUrl),
    },
  );

  const clubsNearby = Array.isArray(clubsNearbyData?.data)
    ? clubsNearbyData.data
    : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!clubKey || isLoading || !isFetched) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  if (isError || !club?.data?._id) {
    return <NotFound />;
  }

  const handleLinkClick = (e) => {
    if (!isLoggedIn) {
      e.preventDefault();
      navigate("/signin");
    }
  };

  const openClosureReport = () => {
    if (!isLoggedIn) {
      navigate("/signin");
      return;
    }
    setIsClosureReportOpen(true);
  };

  const isTodaysEvent = (eventDate) => {
    const today = moment();
    const event = moment(eventDate);

    return today.isSame(event, "day");
  };

  const haveTodaysEvent = () => {
    if (!events?.data) {
      return [];
    }

    return events?.data?.filter((e) => isTodaysEvent(e.eventStartTime));
  };

  return (
    <Layout>
      <>
        <Hero title="Club Details" />
        {/* Details List */}
        <section
          className="details-sec section-gap wow fadeInUp"
          data-wow-delay="0.2s"
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-7">
                <div className="details-head-sec">
                  <div className="text-sec">
                    {club?.data?.name && <h3 className="title-30">{club?.data?.name}</h3>}
                    {club?.data?.category && <p className="p-16">{club.data.category}</p>}
                  </div>
                  <FavouriteIcon
                    endpoint={`api/user/likes/clubs/${club?.data?._id}`}
                    item={club?.data}
                    isLiked={!club?.data?.meta?.isLiked}
                  
                    defaultColor="#000000"
                  />
                  {club?.data?.zomatoRating && (
                    <div className="rating-sec">
                      <p>
                        <i className="fa fa-star"></i>{" "}
                        {club.data.zomatoRating}
                      </p>
                    </div>
                  )}
                </div>
                {club?.data?.address && (
                  <div className="cnt-sec">
                    <p className="p-24">{club.data.address}</p>
                  </div>
                )}
                <div className="about-club-sec">
                  <div className="section-row">
                    <div className="row">
                      {club?.data?.timings && (
                        <div className="col-lg-4">
                          <div className="item">
                            <div className="icon">
                              <i className="far fa-clock"></i>
                            </div>
                            <div className="cnt-sec">
                              <h4 className="title-20">Timings</h4>
                              <p className="p-16">{club.data.timings}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {(club?.data?.cost || club?.data?.averageCost2People) && (
                        <div className="col-lg-4">
                          <div className="item">
                            <div className="icon">
                              <i className="fas fa-users"></i>
                            </div>
                            <div className="cnt-sec">
                              {club?.data?.cost && (
                                <h4 className="title-20">₹{club.data.cost}</h4>
                              )}
                              {club?.data?.averageCost2People && (
                                <p className="p-16">{club.data.averageCost2People}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {club?.data?.happyHoursTimings && (
                        <div className="col-lg-4">
                          <div className="item">
                            <div className="icon">
                              <i className="fas fa-beer"></i>
                            </div>
                            <div className="cnt-sec">
                              <h4 className="title-20">Happy hours</h4>
                              <p className="p-16">{club.data.happyHoursTimings}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {club?.data?.vegNonVeg && (
                        <div className="col-lg-4">
                          <div className="item">
                            <div className="icon">
                              <i className="fas fa-utensils"></i>
                            </div>
                            <div className="cnt-sec">
                              <h4 className="title-20">Menu</h4>
                              <p className="p-16">{club.data.vegNonVeg}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {club?.data?.musicGenre && (
                        <div className="col-lg-4">
                          <div className="item">
                            <div className="icon">
                              <i className="fas fa-music"></i>
                            </div>
                            <div className="cnt-sec">
                              <h4 className="title-20">Music Genre</h4>
                              <p className="p-16">{club.data.musicGenre}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {club?.data?.liveMusicDj && (
                        <div className="col-lg-4">
                          <div className="item">
                            <div className="icon">
                              <i className="fab fa-napster"></i>
                            </div>
                            <div className="cnt-sec">
                              <h4 className="title-20">Live Music/DJ</h4>
                              <p className="p-16">{club.data.liveMusicDj}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="club-stags">
                      <div className="row">
                        <div className="col-lg-4">
                          <div
                            className={`icon ${
                              club?.data?.stagsAllowed?.toLowerCase() === "yes"
                                ? "check"
                                : ""
                            }`}
                          >
                            <i
                              className={`${
                                club?.data?.stagsAllowed?.toLowerCase() ===
                                "yes"
                                  ? "far fa-check-circle"
                                  : "far fa-times-circle"
                              }`}
                            ></i>
                          </div>
                          <h4 className="title-18">Stags</h4>
                        </div>
                        <div className="col-lg-4">
                          <div
                            className={`icon ${
                              club?.data?.seeshaServe?.toLowerCase() === "yes"
                                ? "check"
                                : ""
                            }`}
                          >
                            <i
                              className={`${
                                club?.data?.seeshaServe?.toLowerCase() === "yes"
                                  ? "far fa-check-circle"
                                  : "far fa-times-circle"
                              }`}
                            ></i>
                          </div>
                          <h4 className="title-18">Sheesha</h4>
                        </div>
                        <div className="col-lg-4">
                          <div
                            className={`icon ${
                              club?.data?.kidsFriendly?.toLowerCase() === "yes"
                                ? "check"
                                : ""
                            }`}
                          >
                            <i
                              className={`${
                                club?.data?.kidsFriendly?.toLowerCase() ===
                                "yes"
                                  ? "far fa-check-circle"
                                  : "far fa-times-circle"
                              }`}
                            ></i>
                          </div>
                          <h4 className="title-18">Kids Friendly</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                  {import.meta.env?.VITE_ENABLE_BOOKCLUBFORM ? (
                    <div className="ml-4 mt-3">
                      <button
                        className="btn btn-dark btn-lg"
                        type="button"
                        onClick={() => setIsFormOpened((prev) => !prev)}
                      >
                        Book Club
                      </button>
                    </div>
                  ) : null}
                  <BookClubForm
                    clubId={club?.data?._id}
                    isOpen={isFormOpened}
                    closeModal={() => setIsFormOpened(false)}
                  />
                  <div className="club-direction">
                    <div className="row no-gutters">
                      <div className="col-lg-4">
                        <div
                          className={`sec radius-left ${
                            club?.data?.googleMapLink ? "active" : null
                          }`}
                        >
                          <a
                            href={isLoggedIn ? club?.data?.googleMapLink : null}
                            onClick={handleLinkClick}
                          >
                            <img src={googleMapsIcon} className="size--icon" />
                            <h4 className="title-18">Direction</h4>
                          </a>
                        </div>
                      </div>
                      <div className="col-lg-4">
                        <div
                          className={`sec radius-0 ${
                            club?.data?.whatsappNumber ? "active" : null
                          }`}
                        >
                          <a
                            href={
                              // isLoggedIn
                              //   ?
                              "http://api.whatsapp.com/send?phone=91" +
                              club?.data?.whatsappNumber +
                              `&text=Hey%20there%2C%0A%0AI%20am%20reaching%20out%20to%20enquire%20via%20the%20AZZIR%20app`
                              // : null
                            }
                            // onClick={handleLinkClick}
                          >
                            <img src={whatsappIcon} className="size--icon" />
                            <h4 className="title-18">WhatsApp</h4>
                          </a>
                        </div>
                      </div>
                      <div className="col-lg-4">
                        <div
                          className={`sec radius-right ${
                            club?.data?.phoneNumber ? "active" : null
                          }`}
                        >
                          <a
                            href={
                              isLoggedIn
                                ? "tel:" + club?.data?.phoneNumber
                                : null
                            }
                            onClick={handleLinkClick}
                          >
                            <img src={telephoneIcon} className="size--icon" />
                            <h4 className="title-18">Call</h4>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-5">
                <div className="details-slider-sec">
                  <div id="slider-items" className="itmes-slider no-space">
                    <ImageCarousel
                      images={Array.isArray(club?.data?.media?.ambienceImages) && club?.data?.media?.ambienceImages.length
                        ? club?.data?.media?.ambienceImages
                        : [images.background]
                      }
                      tag={club?.data?.locality || "Club Gallery"}
                      title={club?.data?.name || "Ambience & Experience"}
                      autoplay={true}
                      loop={true}
                      onImageClick={() => setClubViewerIsOpen(true)}
                      onViewClick={() => setClubViewerIsOpen(true)}
                      reserveLabel={
                        import.meta.env?.VITE_ENABLE_BOOKCLUBFORM
                          ? "Book Club"
                          : undefined
                      }
                      onReserveClick={() => setIsFormOpened(true)}
                    />
                    <div className="report-closure-trigger">
                      <button
                        type="button"
                        className="report-closure-trigger__btn"
                        onClick={openClosureReport}
                      >
                        <i className="fas fa-ban" aria-hidden="true" />
                        Report Closure
                      </button>
                    </div>
                    <ReportClubClosure
                      clubId={clubObjectId}
                      clubName={club?.data?.name}
                      isOpen={isClosureReportOpen}
                      closeModal={() => setIsClosureReportOpen(false)}
                    />
                    <div className="owl-theme">
                      <div className="owl-controls">
                        <div className="custom-nav owl-nav"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Details List End */}

        {/* Card List */}

        <section className="card-heading-sec section-gap">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="title-sec wow fadeInUp" data-wow-delay="0.3s">
                  <h2 className="title-25">
                    {haveTodaysEvent()?.length
                      ? "Whats Happening Today"
                      : "Upcoming Events"}
                  </h2>
                </div>
              </div>
            </div>
            <div className="col-lg-12">
              <div className="club-result-row wow fadeInUp">
                <div className="card-list">
                  <div className="row">
                    {
                      // (haveTodaysEvent()?.length
                      //   ? haveTodaysEvent()
                      //   : events?.data?.slice(0, 1) || []
                      // )
                      events?.data?.length ? (
                        events?.data?.map((event, index) => (
                          <EventCard
                            className="col-lg-6 col-xl-3 wow fadeInUp"
                            event={event}
                            key={index}
                            index={index}
                            isCard={true}
                          />
                        ))
                      ) : (
                        <div className="no-data">
                          <p className="no-data-found">No events found</p>
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Card List End */}

        {/* Menu Start */}
        <div
          className="food-menu section-gap wow fadeInUp"
          data-wow-delay="0.2s"
        >
          <div className="container">
            <div className="title-sec wow fadeInUp" data-wow-delay="0.3s">
              <h2 className="title-25">Menu</h2>
            </div>
            <div className="menu-items wow fadeInUp" data-wow-delay="0.5s">
              <div id="menu-slider" className="menu-items-container">
                {Array.isArray(club?.data?.media?.drinkMenuImages) &&
                club?.data?.media?.drinkMenuImages?.length ? (
                  <div
                    className="menu-item"
                    onClick={() => {
                      console.log("Modal clicked");
                      setBeveragesViewerIsOpen(true);
                    }}
                  >
                    <img
                      src={
                        club?.data?.media?.drinkMenuImages?.length
                          ? club?.data?.media?.drinkMenuImages[0]
                          : ""
                      }
                      alt="img"
                    />
                    <div className="text-sec">
                      <h4>Beverages</h4>
                    </div>
                  </div>
                ) : null}
                {Array.isArray(club?.data?.media?.foodMenuImages) &&
                club?.data?.media?.foodMenuImages?.length ? (
                  <div
                    className="menu-item"
                    onClick={() => setFoodMenuViewerIsOpen(true)}
                  >
                    <img
                      src={
                        club?.data?.media?.foodMenuImages?.length
                          ? club?.data?.media?.foodMenuImages[0]
                          : ""
                      }
                      alt="img"
                    />
                    <div className="text-sec">
                      <h4>Food</h4>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {/* Club-Sportlight End */}

        <ImageSwiper
          data={
            Array.isArray(club?.data?.media?.ambienceImages) &&
            club?.data?.media?.ambienceImages?.length
              ? club?.data?.media?.ambienceImages?.map((e) => ({
                  uri: e,
                }))
              : []
          }
          isOpen={clubViewerIsOpen}
          onClose={() => setClubViewerIsOpen(false)}
        />
        <ImageSwiper
          data={
            Array.isArray(club?.data?.media?.drinkMenuImages) &&
            club?.data?.media?.drinkMenuImages?.length
              ? club?.data?.media?.drinkMenuImages?.map((e) => ({
                  uri: e,
                }))
              : []
          }
          isOpen={beveragesViewerIsOpen}
          onClose={() => setBeveragesViewerIsOpen(false)}
        />
        <ImageSwiper
          data={
            Array.isArray(club?.data?.media?.foodMenuImages) &&
            club?.data?.media?.foodMenuImages?.length
              ? club?.data?.media?.foodMenuImages?.map((e) => ({
                  uri: e,
                }))
              : []
          }
          isOpen={foodMenuViewerIsOpen}
          onClose={() => setFoodMenuViewerIsOpen(false)}
        />
        {/* <div
          className="modal fade"
          id="largeModal"
          tabindex="-1"
          role="dialog"
          aria-labelledby="basicModal"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-body">
                <div
                  id="carouselExampleIndicators"
                  className="carousel slide"
                  data-ride="carousel"
                >
                  <ol className="carousel-indicators">
                    <li
                      data-target="#carouselExampleIndicators"
                      data-slide-to="0"
                      className="active"
                    ></li>
                    <li
                      data-target="#carouselExampleIndicators"
                      data-slide-to="1"
                    ></li>
                    <li
                      data-target="#carouselExampleIndicators"
                      data-slide-to="2"
                    ></li>
                  </ol>
                  <div className="carousel-inner">
                    <div className="carousel-item active">
                      <img
                        className="img-size"
                        src="https://images.unsplash.com/photo-1485470733090-0aae1788d5af?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1391&q=80"
                        alt="First slide"
                      />
                    </div>
                    <div className="carousel-item">
                      <img
                        className="img-size"
                        src="https://images.unsplash.com/photo-1491555103944-7c647fd857e6?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80"
                        alt="Second slide"
                      />
                    </div>
                    <div className="carousel-item">
                      <img
                        className="img-size"
                        src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80"
                        alt="Second slide"
                      />
                    </div>
                  </div>
                  <a
                    className="carousel-control-prev"
                    href="#carouselExampleIndicators"
                    role="button"
                    data-slide="prev"
                  >
                    <span
                      className="carousel-control-prev-icon"
                      aria-hidden="true"
                    ></span>
                    <span className="sr-only">Previous</span>
                  </a>
                  <a
                    className="carousel-control-next"
                    href="#carouselExampleIndicators"
                    role="button"
                    data-slide="next"
                  >
                    <span
                      className="carousel-control-next-icon"
                      aria-hidden="true"
                    ></span>
                    <span className="sr-only">Next</span>
                  </a>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default"
                  data-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div> */}
        {/* Club-Sportlight Start */}
        <div
          className="club-spotlight head-title section-gap wow fadeInUp"
          data-wow-delay="0.2s"
        >
          <div className="container">
            <div className="mb-4 title-sec wow fadeInUp" data-wow-delay="0.3s">
              <h2 className="title-25">Nearby Clubs</h2>
            </div>
            <div
              className="px-3 mb-2 text-center wow fadeInUp"
              data-wow-delay="0.4s"
            >
              <strong>Find the Hottest Venues Around You.</strong>
            </div>
            <div
              className="px-3 text-center wow fadeInUp"
              data-wow-delay="0.4s"
            >
              Looking for a night out that’s just around the corner? Explore
              top-rated clubs near your current location — sorted for
              convenience, handpicked for experience. Wherever you are, the
              party is never far.
            </div>
          </div>
          <div className="club-items wow fadeInUp" data-wow-delay="0.5s">
            <div
              id="club-nearby-slider"
              className="club-result-row itmes-slider"
            >
              {isNearbyClubsFetching ? (
                <div className="no-data">
                  <p className="no-data-found">Loading nearby clubs...</p>
                </div>
              ) : clubsNearby?.length != 0 ? (
                <ClubsCarousel data={clubsNearby} />
              ) : (
                <div className="no-data">
                  <p className="no-data-found">No clubs found</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Club-Sportlight Start */}
        {/* <div
          className="club-sportlight head-title club-nearby section-gap wow fadeInUp"
          data-wow-delay="0.2s"
        >
          <div className="container">
            <div className="title-sec wow fadeInUp" data-wow-delay="0.3s">
              <h2 className="title-25">Club Nearby</h2>
            </div>
            <p className="wow fadeInUp" data-wow-delay="0.4s">
              We take pride in providing exceptional service, ensuring your
              every need is met with a smile. Our dedicated staff is here to
              guide you through our menu, recommend pairings, and make your
              dining experience truly exceptional.
            </p>
          </div>
          <div className="club-items wow fadeInUp" data-wow-delay="0.5s">
            <div id="club-nearby-slider" className="itmes-slider">
              <OwlCarousel
                className="owl-carousel owl-theme"
                {...{
                  stagePadding: 50,
                  loop: true,
                  margin: 10,
                  nav: true,
                  navText: [
                    '<i class="fa fa-angle-left" aria-hidden="true"></i>',
                    '<i class="fa fa-angle-right" aria-hidden="true"></i>',
                  ],
                  navContainer: "#club-nearby-slider .custom-nav",
                  responsive: {
                    0: {
                      items: 1,
                    },
                    600: {
                      items: 3,
                    },
                    1000: {
                      items: 5,
                    },
                  },
                }}
              >
                {clubsNearby?.map((data, index) => (
                  // <div className="item">
                  //   <a href={`/clubs/${club?._id}`}>
                  //     <img
                  //       src={
                  //         Array.isArray(club?.media?.ambienceImages) &&
                  //         club?.media?.ambienceImages?.length
                  //           ? club?.media?.ambienceImages[0]
                  //           : images.background
                  //       }
                  //       alt="img"
                  //     />
                  //     <div className="text-sec">
                  //       <h4>{club?.name}</h4>
                  //       <p className="mb-0">
                  //         {club?.locality}, {club?.city}
                  //       </p>
                  //     </div>
                  //   </a>
                  // </div>
                  <div
                    className="wow fadeInUp"
                    data-wow-delay="0.3s"
                    key={index.toString()}
                  >
                    <div className="card">
                      <div className="">
                        <FavouriteIcon
                          endpoint={`api/user/likes/clubs/${data?._id}`}
                          item={data}
                          isLiked={!data?.meta?.isLiked}
                        />
                        <Link to={getClubPath(data)}>
                          <div className="img-sec">
                            <img
                              src={
                                Array.isArray(data?.media?.ambienceImages) &&
                                data?.media?.ambienceImages.length
                                  ? data.media.ambienceImages[0]
                                  : images.background
                              }
                              className="img-fluid"
                              alt={`clubimage`}
                            />
                          </div>
                          <div className="text-sec">
                            <div className="cnt-sec">
                              <h4>{data?.name}</h4>
                              <>
                                <p className="mb-0">{data?.category || "Restrobar"}</p>
                                {(data?.locality || data?.city) && (
                                  <p className="mb-0">
                                    {[data?.locality, data?.city].filter(Boolean).join(", ")}
                                  </p>
                                )}
                              </>
                            </div>
                            {data?.zomatoRating ? (
                              <div className="right-sec">
                                <div className="rating-sec">
                                  <p>
                                    <i className="fa fa-star"></i>
                                    {data?.zomatoRating}
                                  </p>
                                </div>
                                {data?.cost ? (
                                  <div className="price-sec">
                                    <p>₹{data?.cost}</p>
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </OwlCarousel>
              <div className="owl-theme">
                <div className="owl-controls">
                  <div className="custom-nav owl-nav"></div>
                </div>
              </div>
            </div>
          </div>
        </div> */}
        {/* Club-Sportlight End */}
      </>
    </Layout>
  );
};

export default ClubDetails;
